import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Plus, Edit3, ArchiveRestore, Archive, HardHat, RefreshCw, Mail, ShieldAlert } from "lucide-react";
import { GhlSyncButton } from "@/components/GhlIntegration";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORIES = [
  "Plumbing", "Electrical", "HVAC", "Landscaping", "Pool / Spa",
  "Pest Control", "Janitorial / Cleaning", "General Handyman",
  "Roofing", "Painting", "Locksmith", "Appliance Repair",
  "Fire / Life Safety", "Elevator", "Security / Alarm",
  "Snow / Ice Removal", "Waste Management", "Legal", "Accounting", "Other",
];

export default function VendorsPage() {
  const { propertyId, currentProperty, properties } = useProperty();
  const { user } = useAuth() || {};
  const pid = currentProperty?.id || propertyId;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);   // vendor doc or "new"

  const canManage = user?.role === "admin" || user?.role === "property_manager";

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const { data } = await api.get("/vendors", {
        params: { property_id: pid, include_inactive: showInactive ? "true" : "false" },
      });
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setLoading(false);
  }, [pid, showInactive]);

  useEffect(() => { load(); }, [load]);

  const deactivate = async (v) => {
    if (!window.confirm(`Deactivate ${v.name}? Historical PM tickets stay intact; the vendor drops out of the default directory.`)) return;
    try {
      await api.delete(`/vendors/${v.id}`);
      toast.success(`${v.name} deactivated`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const reactivate = async (v) => {
    try {
      await api.post(`/vendors/${v.id}/reactivate`);
      toast.success(`${v.name} reactivated`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const sendOnboarding = async (v) => {
    if (!v.email) return toast.error("Add an email address before sending onboarding");
    try {
      const { data } = await api.post(`/admin/vendors/${v.id}/send-onboarding`);
      toast.success("Onboarding invite emailed");
      if (data.onboarding_url) {
        try { await navigator.clipboard.writeText(data.onboarding_url); toast.message("Link copied to clipboard"); } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/VendorsPage.jsx: swallowed', err); }
      }
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const [nagBusy, setNagBusy] = useState(false);
  const nagOne = async (v) => {
    if (!v.email) return toast.error("Add an email address before nagging");
    setNagBusy(true);
    try {
      const { data } = await api.post(`/vendors/${v.id}/coi-nag`, { property_id: pid, force: true });
      if (data.ok) toast.success(`Nag sent to ${v.name}`);
      else if (data.queued) toast.message(`Queued for ${v.name} — will send when email provider is online`);
      else toast.error(data.error || data.skipped || "Nag failed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setNagBusy(false);
  };
  const nagAll = async () => {
    if (!window.confirm(`Send a COI reminder email to every vendor whose Certificate of Insurance is missing or expired? (${stats.missingInsurance} vendors)`)) return;
    setNagBusy(true);
    try {
      const { data } = await api.post("/vendors/coi-nag/send", {
        property_id: pid,
        buckets: ["missing", "expired", "expiring_7d", "expiring_14d", "expiring_30d"],
      });
      const sent = data.sent || 0;
      const skipped = data.skipped || 0;
      if (sent > 0) {
        toast.success(`${sent} vendor${sent === 1 ? "" : "s"} nagged${skipped ? ` · ${skipped} skipped (already nagged in last 24h or no email)` : ""}`);
      } else if (skipped > 0) {
        toast.message(`All ${skipped} vendors were nagged in the last 24 hours — try again tomorrow.`);
      } else {
        toast.message("No vendors matched the missing / expired filter.");
      }
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setNagBusy(false);
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((v) =>
      [v.name, v.category, v.contact, v.email, v.phone].some(
        (f) => (f || "").toLowerCase().includes(needle)
      )
    );
  }, [items, q]);

  const stats = useMemo(() => {
    const active = items.filter((v) => v.active !== false).length;
    const inactive = items.length - active;
    const avgRating = items.length
      ? (items.reduce((s, v) => s + (Number(v.rating) || 0), 0) / items.length).toFixed(1)
      : "—";
    const missingInsurance = items.filter(
      (v) => !v.insurance_expiry || new Date(v.insurance_expiry) < new Date()
    ).length;
    return { active, inactive, avgRating, missingInsurance };
  }, [items]);

  return (
    <div className="space-y-5" data-testid="vendors-page">
      <PageHeader
        eyebrow="Directory"
        title="Vendors"
        subtitle="Approved vendors and service providers. Add, edit, and retire vendors under your management."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="w-56 bg-white/[0.02] border-white/10 h-9"
              data-testid="vendors-search"
            />
            <label className="text-[11px] flex items-center gap-1.5 text-zinc-400 select-none cursor-pointer">
              <Checkbox checked={showInactive}
                        onCheckedChange={(v) => setShowInactive(!!v)}
                        data-testid="vendors-show-inactive" />
              Show inactive
            </label>
            <Button variant="outline" size="sm" onClick={load} className="border-white/10 h-9 gap-1" data-testid="vendors-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
            {canManage && stats.missingInsurance > 0 && (
              <Button
                onClick={nagAll}
                disabled={nagBusy}
                variant="outline"
                size="sm"
                className="border-rose-500/40 bg-rose-500/[0.08] text-rose-200 hover:bg-rose-500/20 h-9 gap-1.5"
                title={`Email ${stats.missingInsurance} vendor${stats.missingInsurance === 1 ? "" : "s"} whose COI is missing or expired`}
                data-testid="vendors-nag-all-btn">
                <ShieldAlert className="h-3.5 w-3.5" />
                {nagBusy ? "Sending…" : `Nag ${stats.missingInsurance} missing COI`}
              </Button>
            )}
            {canManage && (
              <Button onClick={() => setEditing("new")}
                      className="bg-orange-500 hover:bg-orange-600 gap-2 h-9"
                      data-testid="vendors-new-btn">
                <Plus className="h-4 w-4" /> Add vendor
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Active vendors" value={stats.active} sub={`${stats.inactive} inactive`} />
        <Kpi label="Avg rating" value={stats.avgRating} sub="of 5.0 stars" />
        <Kpi label="Missing / expired COI" value={stats.missingInsurance}
             tone={stats.missingInsurance > 0 ? "bad" : "good"} sub="collect before hire" />
        <Kpi label="Total in directory" value={items.length} sub={showInactive ? "incl. inactive" : "active only"} />
      </div>

      <Card className="border-white/10 bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Vendor</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead className="text-right">Rating</TableHead>
              <TableHead>Insurance</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>GHL</TableHead>
              {canManage && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow className="border-white/5"><TableCell colSpan={canManage ? 8 : 7} className="text-center py-8 text-sm text-zinc-500">Loading…</TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow className="border-white/5">
                <TableCell colSpan={canManage ? 8 : 7} className="text-center py-10" data-testid="vendors-empty">
                  <HardHat className="h-8 w-8 mx-auto mb-2 text-zinc-500" />
                  <div className="text-sm font-semibold">No vendors yet</div>
                  <div className="text-xs text-zinc-400 mt-1">
                    {canManage ? "Add your first vendor to build the directory." : "Ask an admin to add a vendor."}
                  </div>
                  {canManage && (
                    <Button onClick={() => setEditing("new")} className="mt-3 bg-orange-500 hover:bg-orange-600" size="sm">
                      <Plus className="h-4 w-4 mr-1.5" /> Add vendor
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((v) => {
              const inactive = v.active === false;
              const insExpired = v.insurance_expiry && new Date(v.insurance_expiry) < new Date();
              return (
                <TableRow key={v.id} className={"border-white/5 " + (inactive ? "opacity-60" : "")} data-testid={`vendor-row-${v.id}`}>
                  <TableCell className="font-medium">
                    {v.name}
                    {v.website && <a href={v.website} target="_blank" rel="noreferrer" className="ml-2 text-[10px] text-orange-400 hover:underline">↗</a>}
                  </TableCell>
                  <TableCell>{v.category}</TableCell>
                  <TableCell>
                    <div className="text-sm">{v.contact}</div>
                    <div className="text-xs text-zinc-500">{v.email} · {v.phone}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <div className="flex items-center justify-end gap-1"><Star className="h-3.5 w-3.5 text-amber-400" fill="currentColor" /> {v.rating}</div>
                  </TableCell>
                  <TableCell className={"text-sm " + (insExpired ? "text-rose-400" : "")}>
                    {v.insurance_expiry ? new Date(v.insurance_expiry).toLocaleDateString() : <span className="text-rose-400">MISSING</span>}
                    {(!v.insurance_expiry || insExpired) && v.last_coi_nag_at && (
                      <div className="text-[10px] text-zinc-500 mt-0.5" data-testid={`vendor-nag-status-${v.id}`}>
                        Last nudged {new Date(v.last_coi_nag_at).toLocaleDateString()}
                        {v.coi_nag_count > 1 && ` · ${v.coi_nag_count}×`}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={inactive ? "border-white/10 text-zinc-500" : "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"}>
                      {inactive ? "Inactive" : "Active"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {v.owner_user_id
                      ? <GhlSyncButton uid={v.owner_user_id} contactId={v.ghl_contact_id} compact />
                      : <span className="text-[10px] text-zinc-500">no user</span>}
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => sendOnboarding(v)}
                                disabled={!v.email}
                                title={v.onboarding_status === "completed" ? "Vendor already onboarded — will resend a fresh link" : "Email an onboarding link"}
                                className={"h-7 text-xs gap-1 " + (v.onboarding_status === "completed" ? "text-emerald-300" : "text-sky-300")}
                                data-testid={`vendor-onboard-${v.id}`}>
                          <Mail className="h-3 w-3" /> {v.onboarding_status === "completed" ? "Re-invite" : "Invite"}
                        </Button>
                        {(!v.insurance_expiry || insExpired) && (
                          <Button size="sm" variant="ghost" onClick={() => nagOne(v)}
                                  disabled={!v.email || nagBusy}
                                  title={`Email ${v.name} to upload a fresh COI`}
                                  className="h-7 text-xs gap-1 text-rose-300"
                                  data-testid={`vendor-nag-${v.id}`}>
                            <ShieldAlert className="h-3 w-3" /> Nag COI
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setEditing(v)}
                                className="h-7 text-xs gap-1" data-testid={`vendor-edit-${v.id}`}>
                          <Edit3 className="h-3 w-3" /> Edit
                        </Button>
                        {inactive ? (
                          <Button size="sm" variant="ghost" onClick={() => reactivate(v)}
                                  className="h-7 text-xs gap-1 text-emerald-300" data-testid={`vendor-reactivate-${v.id}`}>
                            <ArchiveRestore className="h-3 w-3" /> Reactivate
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => deactivate(v)}
                                  className="h-7 text-xs gap-1 text-rose-400" data-testid={`vendor-deactivate-${v.id}`}>
                            <Archive className="h-3 w-3" /> Deactivate
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {editing && (
        <VendorDialog
          vendor={editing === "new" ? null : editing}
          currentPropertyId={pid}
          availableProperties={properties || []}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, sub, tone = "default" }) {
  const cls = {
    default: "border-white/10 bg-card",
    good: "border-emerald-500/20 bg-emerald-500/[0.03]",
    bad: "border-rose-500/20 bg-rose-500/[0.03]",
  }[tone];
  return (
    <Card className={`p-4 ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-2xl font-semibold font-display mt-1">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </Card>
  );
}

function VendorDialog({ vendor, currentPropertyId, availableProperties, onClose, onSaved }) {
  const isEdit = !!vendor;
  const [form, setForm] = useState({
    name: "", category: CATEGORIES[0], contact: "", phone: "", email: "",
    property_ids: currentPropertyId ? [currentPropertyId] : [],
    rating: 4.5, insurance_expiry: "", notes: "", website: "", address: "",
    hourly_rate: "", after_hours_available: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (vendor) {
      setForm({
        name: vendor.name || "",
        category: vendor.category || CATEGORIES[0],
        contact: vendor.contact || "",
        phone: vendor.phone || "",
        email: vendor.email || "",
        property_ids: vendor.property_ids || [],
        rating: vendor.rating ?? 4.5,
        insurance_expiry: vendor.insurance_expiry || "",
        notes: vendor.notes || "",
        website: vendor.website || "",
        address: vendor.address || "",
        hourly_rate: vendor.hourly_rate ?? "",
        after_hours_available: !!vendor.after_hours_available,
      });
    }
  }, [vendor]);

  const toggleProperty = (pid) => {
    setForm((f) => ({
      ...f,
      property_ids: f.property_ids.includes(pid)
        ? f.property_ids.filter((p) => p !== pid)
        : [...f.property_ids, pid],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Vendor name required");
    if (!form.category) return toast.error("Category required");
    if (!form.email.trim()) return toast.error("Email required");
    if (form.property_ids.length === 0) return toast.error("Assign at least one property");
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        category: form.category,
        contact: form.contact.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        property_ids: form.property_ids,
        rating: Number(form.rating) || 0,
        insurance_expiry: form.insurance_expiry || null,
        notes: form.notes.trim() || null,
        website: form.website.trim() || null,
        address: form.address.trim() || null,
        hourly_rate: form.hourly_rate === "" ? null : Number(form.hourly_rate),
        after_hours_available: !!form.after_hours_available,
      };
      if (isEdit) {
        await api.patch(`/vendors/${vendor.id}`, body);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", body);
        toast.success("Vendor added");
      }
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  const propList = (availableProperties || []).length > 0
    ? availableProperties
    : (currentPropertyId ? [{ id: currentPropertyId, name: currentPropertyId }] : []);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vendor-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${vendor.name}` : "Add vendor"}</DialogTitle>
          <DialogDescription className="text-xs">
            {isEdit ? "Update contact, coverage, or reassign properties." : "Add a new service provider to your directory."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Vendor / company name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="ABC Plumbing LLC" data-testid="vendor-form-name" />
          </div>
          <div>
            <Label>Category *</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="bg-white/[0.02] border-white/10" data-testid="vendor-form-category"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Rating (0-5)</Label>
            <Input type="number" step="0.1" min="0" max="5" value={form.rating}
                   onChange={(e) => setForm({ ...form, rating: e.target.value })}
                   data-testid="vendor-form-rating" />
          </div>
          <div>
            <Label>Contact name</Label>
            <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
                   placeholder="Jane Doe" data-testid="vendor-form-contact" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   placeholder="(555) 123-4567" data-testid="vendor-form-phone" />
          </div>
          <div className="col-span-2">
            <Label>Email *</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   placeholder="dispatch@abcplumbing.com" data-testid="vendor-form-email" />
          </div>
          <div>
            <Label>Website</Label>
            <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                   placeholder="https://…" data-testid="vendor-form-website" />
          </div>
          <div>
            <Label>Hourly rate ($)</Label>
            <Input type="number" step="1" value={form.hourly_rate}
                   onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                   placeholder="125" data-testid="vendor-form-hourly" />
          </div>
          <div className="col-span-2">
            <Label>Business address</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                   placeholder="123 Trade Ave, Anytown FL 33456" data-testid="vendor-form-address" />
          </div>
          <div>
            <Label>Insurance / COI expires</Label>
            <Input type="date" value={form.insurance_expiry}
                   onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })}
                   data-testid="vendor-form-insurance" />
          </div>
          <div className="flex items-center gap-2 mt-6">
            <Checkbox id="afterhours" checked={form.after_hours_available}
                      onCheckedChange={(v) => setForm({ ...form, after_hours_available: !!v })}
                      data-testid="vendor-form-afterhours" />
            <Label htmlFor="afterhours" className="cursor-pointer text-sm">After-hours / emergency available</Label>
          </div>
          <div className="col-span-2">
            <Label>Assigned properties * <span className="text-[10px] text-zinc-500 ml-2">(vendor will appear in the directory for each selected property)</span></Label>
            <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-2 max-h-40 overflow-y-auto">
              {propList.length === 0 && (
                <div className="text-xs text-zinc-500">No properties available for your account.</div>
              )}
              {propList.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={form.property_ids.includes(p.id)}
                    onCheckedChange={() => toggleProperty(p.id)}
                    data-testid={`vendor-form-property-${p.id}`}
                  />
                  {p.name} <span className="text-[10px] text-zinc-500">({p.id.slice(0, 12)})</span>
                </label>
              ))}
            </div>
          </div>
          <div className="col-span-2">
            <Label>Internal notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Referred by Sarah in 4A · charges after 5pm surcharge · billing net-30" data-testid="vendor-form-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="vendor-form-save">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add vendor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
